
import React, { useEffect, useState } from "react";
import {
  Row, Col, Button, notification, Form, Input, Select, Switch, InputNumber, Divider,
  Modal, List, Popconfirm
} from "antd";
import axios from "axios";
import { BookOutlined, SaveOutlined, SettingOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { baseUrl } from "../../config";

const sectionLabel = (title) => (
  <Divider orientation="left" style={{ color: '#7a0000', fontWeight: 700, fontSize: 13, borderColor: '#d0dce8', margin: '16px 0 8px' }}>
    {title}
  </Divider>
);

const { Option } = Select;
const { TextArea } = Input;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('accessToken')}` });

function PurposesForm({ purposeId, onRefreshData = () => {} }) {
  const [form] = Form.useForm();
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [loading, setLoading] = useState(false);

  // Category management
  const [categories, setCategories] = useState([]);
  const [catModalOpen, setCatModalOpen] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [addingCat, setAddingCat] = useState(false);

  useEffect(() => {
    loadCategories();
    if (purposeId) {
      setIsUpdateMode(true);
      fetchPurposeDetails();
    }
  }, [purposeId]);

  const loadCategories = async () => {
    try {
      const res = await axios.get(`${baseUrl}/purpose-categories/list`, { headers: authHeaders() });
      if (res.data.status) setCategories(res.data.result);
    } catch (_) {}
  };

  const handleAddCategory = async () => {
    if (!newCatName.trim()) return;
    setAddingCat(true);
    try {
      await axios.post(`${baseUrl}/purpose-categories/create`, { name: newCatName.trim() }, { headers: authHeaders() });
      setNewCatName('');
      loadCategories();
    } catch (err) {
      notification.error({ message: 'Error', description: err.response?.data?.message || 'Failed to add category' });
    } finally {
      setAddingCat(false);
    }
  };

  const handleDeleteCategory = async (id) => {
    try {
      await axios.delete(`${baseUrl}/purpose-categories/delete/${id}`, { headers: authHeaders() });
      loadCategories();
    } catch (_) {}
  };

  const fetchPurposeDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${baseUrl}/purposes/read/${purposeId}`, {
        headers: authHeaders(),
      });

      if (response.status === 200) {
        const data = response.data.result;
        form.setFieldsValue(data);
      }
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'Failed to fetch purpose details',
        placement: 'topRight'
      });
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (values) => {
    try {
      setLoading(true);

      const formData = {
        ...values,
        code: values.code?.toUpperCase()
      };

      let response;
      if (isUpdateMode) {
        response = await axios.patch(`${baseUrl}/purposes/update/${purposeId}`, formData, { headers: authHeaders() });
      } else {
        response = await axios.post(`${baseUrl}/purposes/create`, formData, { headers: authHeaders() });
      }

      if (response.status === 200) {
        notification.success({
          message: 'Success',
          description: isUpdateMode ? 'Purpose updated successfully!' : 'Purpose created successfully!',
          placement: 'topRight'
        });
        onRefreshData();
        if (!isUpdateMode) form.resetFields();
      }
    } catch (error) {
      notification.error({
        message: 'Error',
        description: error.response?.data?.message || 'Operation failed',
        placement: 'topRight'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '4px 0 24px' }}>
      <Form form={form} onFinish={onFinish} layout="vertical" requiredMark={false} size="middle"
        initialValues={{ status: 'Active', is_recurring: false, requires_approval: true }}>

        {sectionLabel('Purpose Details')}
        <Row gutter={[12, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item label="Purpose Name" name="name" rules={[{ required: true, message: 'Required' }]}>
              <Input prefix={<BookOutlined />} placeholder="Purpose name" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label="Purpose Code" name="code"
              rules={[{ required: true, message: 'Required' }, { min: 3 }, { max: 50 }]}>
              <Input placeholder="e.g., EDU001" style={{ textTransform: 'uppercase' }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label="Category" name="category" rules={[{ required: true, message: 'Required' }]}>
              <Select
                placeholder="Select category"
                dropdownRender={menu => (
                  <>
                    {menu}
                    <div style={{ padding: '4px 8px', borderTop: '1px solid #f0f0f0' }}>
                      <Button size="small" icon={<SettingOutlined />} type="link"
                        onClick={() => setCatModalOpen(true)}>
                        Manage Categories
                      </Button>
                    </div>
                  </>
                )}
              >
                {categories.map(c => (
                  <Option key={c.id} value={c.name}>{c.name.replace(/_/g, ' ')}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label="Sub Category" name="sub_category">
              <Input placeholder="Sub category (optional)" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label="Max Amount Limit" name="max_amount_limit">
              <InputNumber placeholder="No limit if blank" style={{ width: '100%' }} min={0}
                formatter={v => `₹ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={v => v.replace(/₹\s?|(,*)/g, '')} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label="Status" name="status">
              <Select><Option value="Active">Active</Option><Option value="Inactive">Inactive</Option></Select>
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="Description" name="description">
              <TextArea placeholder="Purpose description" rows={2} />
            </Form.Item>
          </Col>
        </Row>

        {sectionLabel('Settings')}
        <Row gutter={[12, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item label="Is Recurring Expense?" name="is_recurring" valuePropName="checked"
              extra="Mark for regular/repeated expenses">
              <Switch checkedChildren="Recurring" unCheckedChildren="One-time" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label="Requires Approval?" name="requires_approval" valuePropName="checked"
              extra="Expenses will need approval before processing">
              <Switch checkedChildren="Required" unCheckedChildren="Auto Approve" />
            </Form.Item>
          </Col>
        </Row>

        <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end' }}>
          <Button htmlType="submit" loading={loading} icon={<SaveOutlined />} size="large"
            style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff', minWidth: 160 }}>
            {isUpdateMode ? 'Update Purpose' : 'Save Purpose'}
          </Button>
        </div>
      </Form>

      {/* Manage Categories Modal */}
      <Modal
        title={<span style={{ color: '#7a0000', fontWeight: 700 }}>Manage Purpose Categories</span>}
        open={catModalOpen}
        onCancel={() => setCatModalOpen(false)}
        footer={null}
        width={420}
      >
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <Input
            placeholder="New category name"
            value={newCatName}
            onChange={e => setNewCatName(e.target.value)}
            onPressEnter={handleAddCategory}
          />
          <Button icon={<PlusOutlined />} loading={addingCat} onClick={handleAddCategory}
            style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }}>
            Add
          </Button>
        </div>
        <List
          size="small"
          bordered
          dataSource={categories}
          renderItem={cat => (
            <List.Item
              actions={[
                <Popconfirm title="Remove this category?" onConfirm={() => handleDeleteCategory(cat.id)} okText="Yes" cancelText="No">
                  <Button danger size="small" icon={<DeleteOutlined />} />
                </Popconfirm>
              ]}
            >
              {cat.name.replace(/_/g, ' ')}
            </List.Item>
          )}
        />
      </Modal>
    </div>
  );
}

export default PurposesForm;
